import React, { useState, useEffect, useRef } from "react";

// Beep sound
const playBeep = () => {
  const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  const oscillator = audioCtx.createOscillator();
  oscillator.type = "sine";
  oscillator.frequency.setValueAtTime(1000, audioCtx.currentTime);
  oscillator.connect(audioCtx.destination);
  oscillator.start();
  oscillator.stop(audioCtx.currentTime + 0.2);
};

export default function Recorder({
  duration = 35,
  startAt = 35, // countdown before recording
  text,
  setText,
  ...props
}) {
  const [seconds, setSeconds] = useState(duration);
  const [countdown, setCountdown] = useState(startAt);
  const [recordingStarted, setRecordingStarted] = useState(false);
  const [recordingStopped, setRecordingStopped] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [transcript, setTranscript] = useState("");

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const countdownRef = useRef(null);

  // Recording timer
  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setSeconds((prev) => {
        if (prev <= 1) {
          stopRecording();
          return duration;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };

  // Transcription API call
  const handleStopRecording = async (blob) => {
    const formData = new FormData();
    formData.append("audio", blob, "recording.wav");

    try {
      setTranscript("Transcribing..."); // optional loading state
      const res = await fetch("/api/transcribe", {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      setTranscript(data.text);
      setText(data.text); // ✅ send to parent immediately

    } catch (err) {
      console.error("Transcription error:", err);
      setTranscript("Transcription failed");
    }
  };

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };

    //   mediaRecorder.onstop = async () => {
    //     const blob = new Blob(chunksRef.current, { type: "audio/wav" });
    //     chunksRef.current = [];

    //     // Save audio for preview
    //     setAudioUrl(URL.createObjectURL(blob));
    // // Send to backend
    //      handleStopRecording(blob);
    //     // Send to backend
    //     const formData = new FormData();
    //     formData.append("audio", blob, "recording.wav");

    //     try {
    //       const res = await fetch("/api/transcribe", {
    //         method: "POST",
    //         body: formData,
    //       });
    //       const data = await res.json();
    //       setTranscript(data.text);
    //     } catch (err) {
    //       console.error("Transcription error:", err);
    //     }
    //   };

    mediaRecorder.onstop = async () => {
      const blob = new Blob(chunksRef.current, { type: "audio/wav" });
      chunksRef.current = [];
      setAudioUrl(URL.createObjectURL(blob));
      // ✅ Store blob for parent use
      props.onRecordingComplete?.(blob);
    };
    

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      playBeep();
      startTimer();

      setRecordingStarted(true);
      setRecordingStopped(false);
      if (countdownRef.current) clearInterval(countdownRef.current);
    } catch (error) {
      console.error("Mic access denied:", error);
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && recordingStarted) {
      mediaRecorderRef.current.stop();
    }
    stopTimer();
    if (countdownRef.current) clearInterval(countdownRef.current);
    setRecordingStarted(false);
    setRecordingStopped(true);
  };

  // Countdown effect before recording starts
  useEffect(() => {
    if (countdown <= 0 && !recordingStarted && !recordingStopped) {
      startRecording();
      return;
    }

    if (!recordingStarted && !recordingStopped) {
      countdownRef.current = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(countdownRef.current);
    }
  }, [countdown, recordingStarted, recordingStopped]);

  // Reset for retake
  const handleRetake = () => {
    setTranscript("");
    setAudioUrl(null);
    setRecordingStopped(false);
    setCountdown(startAt);
    setSeconds(duration);
  };

  // Progress bar
  const getProgressBarStyle = () => {
    const percentage = (seconds / duration) * 100;
    let color = "#38a169";
    if (percentage <= 50) color = "#f6ad55";
    if (percentage <= 25) color = "#e53e3e";

    return {
      width: `${percentage}%`,
      backgroundColor: color,
      transition: "width 0.5s ease",
      height: "15px",
      borderRadius: "999px",
    };
  };

  return (
    <div className={`${props.className} max-w-md mx-auto p-2 bg-white shadow-lg rounded-xl`}>
      <div className="bg-gray-100 p-4 rounded-lg">
        <p className="font-bold text-2xl text-center text-gray-800">Recorded Answer</p>
        <p className="text-gray-600">Current Status:</p>

        {!recordingStarted && !recordingStopped && (
          <p className="text-center text-purple-800 font-semibold mt-2">
            Recording starts in {countdown}s
          </p>
        )}

        {recordingStarted && (
          <p className="text-gray-800 font-medium">Time Left: {seconds}s</p>
        )}

        <div className="w-full bg-gray-200 rounded-full mt-3">
          {recordingStarted && <div style={getProgressBarStyle()}></div>}
        </div>

        <div className="flex justify-center gap-4 mt-5">
          {!recordingStarted && !recordingStopped && (
            <button
              onClick={startRecording}
              className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-6 rounded-sm transition"
            >
              Skip
            </button>
          )}
          {recordingStarted && (
            <button
              onClick={stopRecording}
              className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-6 rounded-sm transition"
            >
              Stop
            </button>
          )}
          {recordingStopped && (
            <button
              onClick={handleRetake}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-sm transition"
            >
              Retake
            </button>
          )}
        </div>

        {recordingStarted && (
          <div className="text-center text-green-500 mt-3">Recording...</div>
        )}

        {audioUrl && (
          <div className="mt-4">
            <p className="font-semibold">Preview:</p>
            <audio controls src={audioUrl}></audio>
          </div>
        )}

        {/* {transcript && (
          <div className="mt-4">
            <p className="font-semibold">Transcription:</p>
            <p className="text-gray-700">{transcript}</p>
          </div>
        )} */}
      </div>
    </div>
  );
}
